import { ReactNode, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PageHeader from "../../../Component/PageHeader";
import StatStrip from "../../../Component/StatStrip";
import { getRequest } from "../../../utils/api";
import "../plans.css";

export interface PlanStats {
  livePlans?: number;
  draftPlans?: number;
  paidTenants?: number;
  planRevenue?: number;
  addonRevenue?: number;
  changeRequests?: number;
}

// Tabs of the Plan & Pricing section; each one keeps its own route
export const PLAN_TABS: [string, string, string, string][] = [
  ["/plans-pricing", "ri-price-tag-3-line", "All Plan & Pricing", "Plans shown on the client app's pricing cards and Choose Plan modal."],
  ["/plan-capablities-matrix", "ri-layout-grid-line", "Capabilities & Matrix", "What each plan includes — this comparison table goes to the pricing page."],
  ["/plan-add-ons", "ri-puzzle-line", "Add-ons", "Extras a client can buy on top of a plan — seats, numbers, AI credits."],
  ["/plan-meta-pricing", "ri-meta-line", "Meta Pricing", "Meta base rate + your markup = what the client's wallet is charged per message."],
  ["/plan-cost-calculator", "ri-calculator-line", "Cost Calculator", "For sales / support — message cost estimate for any category and plan."],
  ["/plan-usage-restrictions", "ri-speed-up-line", "Usage Restrictions", "Hard limits for every plan. Toggle a limit off to block the feature entirely."],
  ["/plan-legacy-migration", "ri-shuffle-line", "Legacy Migration", "Existing users still on an old V1 plan — map them to a hidden V2 twin and repoint their account."],
];

export const lakh = (num: number) => {
  if (!num) return "₹0";
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)}Cr`;
  if (num >= 100000) return `₹${(num / 100000).toFixed(1)}L`;
  if (num >= 1000) return `₹${(num / 1000).toFixed(1)}K`;
  return `₹${Math.round(num).toLocaleString("en-IN")}`;
};

interface PlanLayoutProps {
  actions?: ReactNode;
  // pass when the page already fetched /plans/list; otherwise the layout fetches it
  stats?: PlanStats;
  children: ReactNode;
}

// Header + stats + tabs shared by every Plan & Pricing tab
const PlanLayout = ({ actions, stats, children }: PlanLayoutProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [ownStats, setOwnStats] = useState<PlanStats>({});

  useEffect(() => {
    if (stats) return;
    getRequest(`/plans/list?page=1&status=all`)
      .then((res: any) => { if (res?.status === "success" && res.stats) setOwnStats(res.stats); })
      .catch(() => { /* stats are decorative here; the tab shows its own errors */ });
  }, [stats]);

  const s = stats || ownStats;
  const tab = PLAN_TABS.find(t => t[0] === pathname) || PLAN_TABS[0];

  return (
    <div className="container-fluid">
      <div className="cx">
        <PageHeader crumb="Plan & Pricing" title="Plan & Pricing" sub={tab[3]} actions={actions} />

        <StatStrip items={[
          { label: "Live plans", value: s.livePlans || 0, icon: "ri-price-tag-3-line", delta: `+${s.draftPlans || 0} draft` },
          { label: "Paid tenants", value: s.paidTenants || 0, icon: "ri-building-2-line", delta: "on paid plans" },
          { label: "Plan revenue", value: lakh(s.planRevenue || 0), icon: "ri-money-rupee-circle-line", delta: "subscriptions" },
          { label: "Add-on revenue", value: lakh(s.addonRevenue || 0), icon: "ri-puzzle-line", delta: "add-ons" },
          { label: "Change requests", value: s.changeRequests || 0, icon: "ri-arrow-up-down-line", delta: "upgrades & downgrades" },
        ]} />

        <div className="cx-tabs">
          {PLAN_TABS.map(([path, icon, label]) => (
            <button type="button" key={path} className={path === tab[0] ? "on" : ""} onClick={() => navigate(path)}>
              <i className={icon}></i>{label}
            </button>
          ))}
        </div>

        {children}
      </div>
    </div>
  );
};

export default PlanLayout;
